import { FileType, getExtensionsForFileType } from '../files/constants.js';

/**
 * File types accepted by the CAPA action evidence uploaders ("Evidences of
 * violation" and the "Remediation request form"). A curated subset of
 * images/documents plus every video format from the shared registry, so the
 * CAPA video set can never drift from the platform-wide one.
 */
export const CAPA_ACTION_ACCEPTED_FILE_TYPES: string[] = [
  '.jpg',
  '.jpeg',
  '.png',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  ...getExtensionsForFileType(FileType.Video),
];

/** Maximum upload size for CAPA action evidence files (100 MB). */
export const CAPA_ACTION_MAX_FILE_SIZE = 100 * 1024 * 1024;

export const CAPA_ACTION_PRIORITY = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
} as const;

export const CAPA_ACTION_PRIORITY_LABELS: Record<
  keyof typeof CAPA_ACTION_PRIORITY,
  string
> = {
  A: 'Low',
  B: 'Moderate',
  C: 'Medium',
  D: 'High',
  E: 'Extreme',
};

export const CAPA_ACTION_TYPES = {
  CAP: 'CAP',
  CA: 'CA',
  PA: 'PA',
} as const;

// CAPA Workflow types
export const CAPA_WORKFLOW_TYPES = {
  EXTRACTION: 'EXTRACTION',
  CAPA_CLASSIFICATION: 'CAPA_CLASSIFICATION',
  CAPA_CAP_FILE_DATA_EXTRACTION: 'CAPA_CAP_FILE_DATA_EXTRACTION',
  EXTRACT_FINDINGS: 'EXTRACT_FINDINGS',
  CAPS_CREATION: 'CAPS_CREATION',
  CAPA_ACTIONS_GENERATION: 'CAPA_ACTIONS_GENERATION',
} as const;

export type CapaWorkflowType =
  (typeof CAPA_WORKFLOW_TYPES)[keyof typeof CAPA_WORKFLOW_TYPES];

/**
 * Stable, locale-independent phase codes the CAPA AI background jobs publish as
 * `stepProgress[step].messageKey` (see `services/capa/workflow-progress.ts`).
 * The backend owns the phase — only it knows what it is doing — and each
 * frontend maps the code to a localized string.
 *
 * This lives in `modules` because it is a frontend/backend contract: the codes
 * used to be bare string literals in the four job files, and frontend-v2's i18n
 * tables silently drifted onto invented names (`extracting`, `analysing`), so
 * every real phase rendered as a raw i18n key (DELTA-2401). Add a code here
 * first, then add the matching i18n key in every locale.
 */
export const CAPA_PROGRESS_MESSAGE_KEYS = {
  /** CAPA_CLASSIFICATION — the audit document is being read. */
  READING_DOCUMENT: 'readingDocument',
  /** CAPA_CLASSIFICATION — extracted audit details are being analyzed. */
  ANALYZING_DETAILS: 'analyzingDetails',
  /** EXTRACT_FINDINGS — preparing the findings analysis. */
  PREPARING_FINDINGS: 'preparingFindings',
  /** EXTRACT_FINDINGS — findings are being extracted by the model. */
  EXTRACTING_FINDINGS: 'extractingFindings',
  /** EXTRACT_FINDINGS — extracted findings are being persisted/normalized. */
  PROCESSING_FINDINGS: 'processingFindings',
  /** CAPS_CREATION — suggested actions are being generated per finding. */
  GENERATING_ACTIONS: 'generatingActions',
  /** CAPA_CAP_FILE_DATA_EXTRACTION — preparing the CAP analysis. */
  PREPARING_CAP: 'preparingCap',
  /** CAPA_CAP_FILE_DATA_EXTRACTION — findings and actions are being extracted. */
  EXTRACTING_CAP: 'extractingCap',
  /** CAPA_CAP_FILE_DATA_EXTRACTION — extracted CAP data is being processed. */
  PROCESSING_CAP: 'processingCap',
  /** CAPA_CAP_FILE_DATA_EXTRACTION — companies and assignees are being matched. */
  MATCHING_ENTITIES: 'matchingEntities',
  /**
   * Frontend-only fallback shown between kicking a workflow off and the first
   * poll that resolves a real per-step code. No job ever publishes it.
   */
  STARTING: 'starting',
} as const;

export type CapaProgressMessageKey =
  (typeof CAPA_PROGRESS_MESSAGE_KEYS)[keyof typeof CAPA_PROGRESS_MESSAGE_KEYS];

export const CAPA_ACTION_STATUS = {
  TO_DO: 'TO_DO',
  IN_PROGRESS: 'IN_PROGRESS',
  BLOCKED: 'BLOCKED',
  IN_REVIEW: 'IN_REVIEW',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  DONE: 'DONE',
} as const;

export const CAPA_ACTION_COMPANY_TYPES = {
  CREATOR: 'CREATOR',
  OWNER: 'OWNER',
  COLLABORATOR: 'COLLABORATOR',
} as const;

export const CAPA_ACTION_USER_TYPES = {
  REPORTER: 'REPORTER',
  ASSIGNEE: 'ASSIGNEE',
  WATCHER: 'WATCHER',
} as const;

export type CapaActionUserType =
  (typeof CAPA_ACTION_USER_TYPES)[keyof typeof CAPA_ACTION_USER_TYPES];

/**
 * Roles a caller can hold on one action. Reported on the action-detail wire as
 * `userRoles` / `companyRoles`; both dashboards gate their edit controls on
 * them, so they are contract, not an internal detail.
 */
export const CAPA_USER_ROLES = {
  CREATOR: 'CREATOR',
  REPORTER: 'REPORTER',
  ASSIGNEE: 'ASSIGNEE',
  COLLABORATOR: 'COLLABORATOR',
  WATCHER: 'WATCHER',
} as const;

export const CAPA_COMPANY_ROLES = {
  CREATOR_COMPANY: 'CREATOR_COMPANY',
  REPORTING_COMPANY: 'REPORTING_COMPANY',
  RESPONSIBLE_COMPANY: 'RESPONSIBLE_COMPANY',
  COLLABORATING_COMPANY: 'COLLABORATING_COMPANY',
} as const;

/**
 * How a stored relationship row becomes a reported role.
 *
 * The link-table `type` columns (`..._actions_users.type`,
 * `..._actions_companies.type`) are storage; the role names above are what the
 * dashboards check. A CREATOR *company* row yields two roles — it both created
 * the action and reports on it.
 */
export const CAPA_USER_TYPE_TO_ROLE = {
  REPORTER: CAPA_USER_ROLES.REPORTER,
  ASSIGNEE: CAPA_USER_ROLES.ASSIGNEE,
  COLLABORATOR: CAPA_USER_ROLES.COLLABORATOR,
  WATCHER: CAPA_USER_ROLES.WATCHER,
} as const;

export const CAPA_COMPANY_TYPE_TO_ROLES = {
  CREATOR: [
    CAPA_COMPANY_ROLES.CREATOR_COMPANY,
    CAPA_COMPANY_ROLES.REPORTING_COMPANY,
  ],
  OWNER: [CAPA_COMPANY_ROLES.RESPONSIBLE_COMPANY],
  COLLABORATOR: [CAPA_COMPANY_ROLES.COLLABORATING_COMPANY],
} as const;

export const CAPA_ACTION_RELATION_TYPES = {
  CHILD: 'CHILD',
  LINKED: 'LINKED',
} as const;

export const CAPA_ACTION_SOURCE_TYPES = {
  AUDIT: 'AUDIT',
  SLCP: 'SLCP',
} as const;

/**
 * A finding's `type`: the kind of source object it hangs off. ZERO_TOLERANCE
 * is system-generated (amfori ZT incidents), not user-creatable, so the create
 * request excludes it; it exists here as the canonical value the importer
 * writes and the findings list renders and filters by.
 */
export const CAPA_FINDING_SOURCE_TYPES = {
  AUDIT: 'AUDIT',
  SLCP: 'SLCP',
  FACTORY_VISIT: 'FACTORY_VISIT',
  OTHER: 'OTHER',
  ZERO_TOLERANCE: 'ZERO_TOLERANCE',
} as const;

/** A finding's urgency, `A` (minor) to `E` (zero tolerance). */
export const CAPA_FINDING_URGENCY_LEVELS = ['A', 'B', 'C', 'D', 'E'] as const;

export const ENTITY_TAGS_ENTITY_TYPES = {
  FINDING: 'FINDING',
  CAPA: 'CAPA',
} as const;

/**
 * A finding's status, as reported on a plan's linked findings.
 *
 * It is never stored: a finding is DONE when every action the reading company
 * can see against it is DONE, TO_DO when they all are (or there are none), and
 * IN_PROGRESS for any other mix. BLOCKED and IN_REVIEW are deliberately absent
 * — an action in either state rolls its finding up to IN_PROGRESS.
 */
export const CAPA_FINDING_STATUS = {
  TO_DO: 'TO_DO',
  IN_PROGRESS: 'IN_PROGRESS',
  DONE: 'DONE',
} as const;

export type CapaFindingStatus =
  (typeof CAPA_FINDING_STATUS)[keyof typeof CAPA_FINDING_STATUS];

/**
 * What kind of change a history row records.
 *
 * The values are **lowercase** — that is what every writer stores and what both
 * dashboards compare against, and the column carries a CHECK constraint on
 * exactly this set (widened to include `deleted` by
 * `202607071000-add-deleted-to-capa-action-history-type-check`).
 *
 * Documented vocabulary, not a schema: `HistoryEntrySchema.type` stays
 * `z.string()`, because the wire has always been unvalidated here and a row
 * written before the CHECK existed must still serialise.
 */
export const CAPA_HISTORY_TYPES = {
  CREATED: 'created',
  ADDED: 'added',
  CHANGED: 'changed',
  REMOVED: 'removed',
  DELETED: 'deleted',
} as const;

export type CapaHistoryType =
  (typeof CAPA_HISTORY_TYPES)[keyof typeof CAPA_HISTORY_TYPES];

/**
 * Plan (CAP) fields that propagate down to CHILD actions, in apply order.
 *
 * Order matters: access-granting fields come first so that a collaborator or
 * assignee gains visibility on the action before the remaining fields are
 * written and their notifications go out.
 */
export const CAPA_PROPAGATION_FIELDS = [
  'collaboratorIds',
  'assignees',
  'reporters',
  'tagIds',
  'dueDate',
] as const;

export type CapaPropagationField = (typeof CAPA_PROPAGATION_FIELDS)[number];

/**
 * How much of the per-action notification stream a bulk update should emit.
 * - `all`: current behaviour for the direct bulk endpoints (additions + removals)
 * - `additions-only`: used when propagating from a plan — announce gained
 *   access, stay silent about removals to keep the email volume sane
 * - `none`: write history only
 */
export const CAPA_BULK_NOTIFY_MODES = {
  ALL: 'all',
  ADDITIONS_ONLY: 'additions-only',
  NONE: 'none',
} as const;

export type CapaBulkNotifyMode =
  (typeof CAPA_BULK_NOTIFY_MODES)[keyof typeof CAPA_BULK_NOTIFY_MODES];

export const ASSET_ACCESS_CONTROL_STATUSES = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;

export const ASSET_ACCESS_CONTROL_TYPES = {
  AUDITS: 'AUDITS',
} as const;

export enum ShareAssessmentStatus {
  REQUESTED = 'REQUESTED',
  SHARED = 'SHARED',
}
